import React, { useState, useRef, useEffect } from "react";
import { useQuery, UseQueryResult } from "react-query";
import { getAllPosts } from "../../Helpers/backendHelper";
import { DEFAULT_POST_PAGE_SIZE, PAGES_LIST } from "../../Helpers/constants";
import { GET_ALL_POSTS } from "./queryKey";
import { MakeNormalPost } from "./MakeNormalPost";
import Post from "./Post";
import { FloatingHead } from "../misc/floatingHead";
import {
  GetAllPostsResponse,
  PostDataInterface,
} from "../../Helpers/backendResponseInterfaces";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import { MakeTradePost } from "./MakeTradePost";
import LoadingScreen from "../utils/loadingScreen";
import { getUserData } from "../../Helpers/utils";
import NorthTwoToneIcon from "@mui/icons-material/NorthTwoTone";

const Posts: React.FC = () => {
  const isMobile = window.innerWidth <= 900;
  const [makeNormalPostModal, setNormalPostModalIsOpen] =
    useState<boolean>(false);
  const [makeTradePostModal, setTradePostModalIsOpen] =
    useState<boolean>(false);
  const user = getUserData();
  const [page, setPage] = useState<number>(1);
  const [posts, setPosts] = useState<PostDataInterface[]>([]);
  const [totalPosts, setTotalPosts] = useState<number>(0);
  const [noMorePosts, setNoMorePosts] = useState<boolean>(false);
  const [newPostMade, setNewPostMade] = useState<boolean>(false);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [isCurrentPageLoaded, setIsCurrentPageLoaded] =
    useState<boolean>(false);
  const topRef = useRef<HTMLDivElement>(null);

  const actions = [
    {
      name: "Post Information",
      icon: <EditNoteIcon />,
      action: setNormalPostModalIsOpen,
    },
    {
      name: "Post Trade",
      icon: <CandlestickChartIcon />,
      action: setTradePostModalIsOpen,
    },
  ];

  const targetRef = useRef<HTMLDivElement>(null);

  const { isLoading: isPostLoading, refetch: refetchPosts } = useQuery({
    queryKey: [GET_ALL_POSTS, page],
    queryFn: () => getAllPosts({ page, size: DEFAULT_POST_PAGE_SIZE }),
    onSuccess: (data: { data: GetAllPostsResponse }) => {
      const respPosts = data.data;
      if (page === 1) {
        setPosts(respPosts.items);
      } else {
        setPosts((posts) => [
          ...posts,
          ...respPosts.items.filter(
            (post) => !posts.some((p) => p.id === post.id),
          ),
        ]);
      }
      setTotalPosts(respPosts.total);
      setIsCurrentPageLoaded(true);
    },
    onError: (error) => {
      console.error(error);
      setIsCurrentPageLoaded(false);
    },
    refetchOnWindowFocus: false,
  }) as UseQueryResult<GetAllPostsResponse, unknown>;

  const resetPosts = () => {
    topRef.current?.scrollIntoView({ behavior: "smooth" });
    if (page !== 1) setPage(1);
    else refetchPosts();
    setNoMorePosts(false);
    setNewPostMade(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowRefreshButton(true);
    }, 30000); // Show refresh button after 30 seconds

    return () => clearTimeout(timer);
  }, []);

  const handleRefresh = () => {
    setShowRefreshButton(false);
    resetPosts();
  };

  useEffect(() => {
    if (totalPosts > 0 && posts.length >= totalPosts) {
      setNoMorePosts(true);
    }
  }, [posts, totalPosts]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (isCurrentPageLoaded && !noMorePosts && entries[0].isIntersecting) {
          setPage((currentPage) => currentPage + 1);
        }
      },
      { threshold: 1 },
    );

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      if (targetRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(targetRef.current);
      }
    };
  }, [isCurrentPageLoaded, noMorePosts]);

  useEffect(() => {
    if (newPostMade) {
      const timer = setTimeout(() => {
        setNewPostMade(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [newPostMade]);

  if (isPostLoading && !isCurrentPageLoaded) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex flex-col justify-start h-max min-h-full items-center bg-slate-100">
      <div ref={topRef} />
      {!posts.length && !isPostLoading ? (
        <div className="md:w-7/12 w-full text-center">No posts found</div>
      ) : (
        <div className="flex flex-col items-center justify-start my-8 md:w-7/12 w-11/12">
          <div
            className={`w-full flex flex-col gap-6 transition-all duration-300 ${
              newPostMade ? "bg-green-100" : "bg-white"
            }`}
          >
            {posts.map((post) => (
              <Post key={post.id} initialPost={post} />
            ))}
          </div>
          <div ref={targetRef} />
        </div>
      )}
      {totalPosts !== 0 && noMorePosts && <div>No more posts found</div>}
      <MakeNormalPost
        isOpen={makeNormalPostModal}
        setIsOpen={setNormalPostModalIsOpen}
        resetPosts={resetPosts}
      />
      <MakeTradePost
        isOpen={makeTradePostModal}
        setIsOpen={setTradePostModalIsOpen}
        resetPosts={resetPosts}
      />
      <FloatingHead
        userType={user?.user.user_type}
        {...PAGES_LIST[0].floatingHead}
        actions={actions}
        isMobile={isMobile}
      />
      {showRefreshButton && (
        <div className="flex flex-row items-center absolute top-28 left-1/3 text-sm bg-blue-500 text-white px-3 py-1 rounded-full">
          <button onClick={handleRefresh}>
            <NorthTwoToneIcon fontSize="inherit" /> Refresh posts{" "}
          </button>
        </div>
      )}
    </div>
  );
};

export default Posts;
